import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useDateRange } from "@/context/DateRangeContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import { Loader2, ChevronRight, ArrowLeft, MapPin } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const NAME_TO_CODE = {
  "United States of America": "US", "United States": "US", "United Kingdom": "GB",
  "Germany": "DE", "France": "FR", "Italy": "IT", "Spain": "ES", "Canada": "CA", "Mexico": "MX",
};

export default function OrdersMap() {
  const { currency } = useCurrency();
  const { start, end } = useDateRange();
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [data, setData] = useState([]);
  const [level, setLevel] = useState("country");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = `currency=${currency}&start=${start}&end=${end}` +
      (country ? `&country=${country}` : "") + (state ? `&state=${encodeURIComponent(state)}` : "");
    api.get(`/analytics/geo?${params}`).then((r) => { setData(r.data.data); setLevel(r.data.level); setLoading(false); })
      .catch(() => { setData([]); setLoading(false); });
  }, [currency, start, end, country, state]);

  const byCode = useMemo(() => {
    const m = {};
    if (level === "country") for (const d of data) m[d.key] = d;
    return m;
  }, [data, level]);
  const maxOrders = useMemo(() => Math.max(1, ...data.map((d) => d.orders)), [data]);

  const fillFor = (geoName) => {
    const code = NAME_TO_CODE[geoName];
    const d = code && byCode[code];
    if (!d) return "#171717";
    const t = 0.2 + 0.8 * (d.orders / maxOrders);
    return `rgba(0,122,255,${t.toFixed(2)})`;
  };

  const crumbs = ["Worldwide"];
  if (country) crumbs.push(country);
  if (state) crumbs.push(state);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4" data-testid="orders-map">
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#1a1a1a] flex items-center gap-2 text-xs text-[#A3A3A3]">
          <MapPin className="h-4 w-4 text-[#007AFF]" />
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <ChevronRight className="h-3 w-3 text-[#555]" />}
              <span className={i === crumbs.length - 1 ? "text-white font-semibold" : ""}>{c}</span>
            </span>
          ))}
        </div>
        <div className="bg-[#0a0a0a]">
          <ComposableMap projectionConfig={{ scale: 140 }} height={420} style={{ width: "100%", height: "auto" }} data-testid="world-map">
            <Geographies geography={GEO_URL}>
              {({ geographies }) => geographies.map((geo) => {
                const code = NAME_TO_CODE[geo.properties.name];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => { if (code && byCode[code]) { setCountry(code); setState(""); } }}
                    style={{
                      default: { fill: fillFor(geo.properties.name), stroke: "#000", strokeWidth: 0.3, outline: "none" },
                      hover: { fill: code && byCode[code] ? "#4da3ff" : "#222", outline: "none", cursor: code && byCode[code] ? "pointer" : "default" },
                      pressed: { fill: "#4da3ff", outline: "none" },
                    }}
                  />
                );
              })}
            </Geographies>
          </ComposableMap>
        </div>
      </div>

      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden flex flex-col" data-testid="geo-drill">
        <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between">
          <h3 className="font-head font-bold text-white text-sm capitalize">{level === "country" ? "Countries" : level === "state" ? "States / Regions" : "Cities / Towns"}</h3>
          {(country || state) && (
            <button data-testid="geo-back" onClick={() => (state ? setState("") : setCountry(""))} className="inline-flex items-center gap-1 text-xs text-[#007AFF] hover:text-[#4da3ff]"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[440px]">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-[#666]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : !data.length ? (
            <p className="p-6 text-center text-sm text-[#666]">No order location data for this range.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0f0f0f]"><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-4 py-2">{level}</th><th className="px-4 py-2 text-right">Orders</th><th className="px-4 py-2 text-right">Units</th><th className="px-4 py-2 text-right">Revenue</th>
              </tr></thead>
              <tbody>
                {data.map((d) => {
                  const drillable = level === "country" || level === "state";
                  return (
                    <tr key={d.key} data-testid={`geo-row-${d.key}`}
                      onClick={() => { if (level === "country") { setCountry(d.key); setState(""); } else if (level === "state") setState(d.key); }}
                      className={`border-t border-[#141414] hover:bg-white/5 ${drillable ? "cursor-pointer" : ""}`}>
                      <td className="px-4 py-2 text-white">{d.name}{drillable && <ChevronRight className="inline h-3 w-3 text-[#555] ml-1" />}</td>
                      <td className="px-4 py-2 text-right tnum text-white font-semibold">{fmtNum(d.orders)}</td>
                      <td className="px-4 py-2 text-right tnum text-[#A3A3A3]">{fmtNum(d.units)}</td>
                      <td className="px-4 py-2 text-right tnum text-[#10B981]">{fmtMoney(d.revenue, currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
